import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const crmCardRef = useRef(null);
  const tiltFrameRef = useRef(null);

  const updateCrmCardTilt = (event) => {
    const card = crmCardRef.current;
    const canTilt = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
      && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!card || !canTilt) return;

    const rect = card.getBoundingClientRect();
    const normalizedX = (event.clientX - rect.left) / rect.width - 0.5;
    const normalizedY = (event.clientY - rect.top) / rect.height - 0.5;
    const rotateY = normalizedX * 5.5;
    const rotateX = normalizedY * -5.5;

    if (tiltFrameRef.current) window.cancelAnimationFrame(tiltFrameRef.current);
    tiltFrameRef.current = window.requestAnimationFrame(() => {
      card.style.setProperty('--crm-rotate-x', `${rotateX.toFixed(2)}deg`);
      card.style.setProperty('--crm-rotate-y', `${rotateY.toFixed(2)}deg`);
      card.style.setProperty('--crm-lift', '-3px');
      card.style.setProperty('--crm-scale', '1.005');
    });
  };

  const resetCrmCardTilt = () => {
    const card = crmCardRef.current;
    if (!card) return;
    if (tiltFrameRef.current) window.cancelAnimationFrame(tiltFrameRef.current);
    card.style.setProperty('--crm-rotate-x', '0deg');
    card.style.setProperty('--crm-rotate-y', '0deg');
    card.style.setProperty('--crm-lift', '0px');
    card.style.setProperty('--crm-scale', '1');
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const response = await api.post('/auth/login', { email, password });
    const token = response.data.access_token;
    localStorage.setItem('token', token);

    // Obtener rol
    const meResponse = await api.get('/auth/me');
    const role = meResponse.data.role || 'admin';
    localStorage.setItem('role', role);

    toast.success('Inicio de sesión exitoso');
    navigate(role === 'cajero' ? '/leads-crm' : '/dashboard');
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <style>{`
        @keyframes loginAmbientGradient {
          0%, 100% { background-position: 0% 100%; }
          50% { background-position: 100% 0%; }
        }
        @keyframes loginGradientDeep {
          0%, 100% { opacity: 0.9; transform: translate3d(-3%, 2%, 0) scale(1); }
          50% { opacity: 0.72; transform: translate3d(4%, -3%, 0) scale(1.08); }
        }
        @keyframes loginGradientAurora {
          0%, 100% { opacity: 0.34; transform: translate3d(5%, -4%, 0) rotate(-5deg) scale(1.08); }
          50% { opacity: 0.58; transform: translate3d(-6%, 5%, 0) rotate(4deg) scale(1.16); }
        }
        @keyframes loginGradientPearl {
          0%, 100% { opacity: 0.16; transform: translate3d(-8%, 8%, 0) scale(1.04); }
          50% { opacity: 0.42; transform: translate3d(7%, -5%, 0) scale(1.14); }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-ambient-gradient,
          .login-gradient-deep,
          .login-gradient-aurora,
          .login-gradient-pearl { animation: none !important; }
          .login-crm-card { transition: none !important; transform: none !important; }
        }
        .login-crm-card {
          --crm-rotate-x: 0deg;
          --crm-rotate-y: 0deg;
          --crm-lift: 0px;
          --crm-scale: 1;
          transform: perspective(1200px) translateY(var(--crm-lift)) rotateX(var(--crm-rotate-x)) rotateY(var(--crm-rotate-y)) rotateZ(-0.5deg) scale(var(--crm-scale));
          transform-style: preserve-3d;
          transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 420ms ease;
          will-change: transform;
        }
      `}</style>

      <div className="flex min-h-screen flex-col lg:flex-row">
        <section
          className="login-ambient-gradient relative hidden min-h-[420px] overflow-hidden bg-[#0B1739] lg:flex lg:w-[58%] lg:items-center lg:justify-center"
          style={{ animation: 'loginAmbientGradient 20s ease-in-out infinite alternate' }}
          aria-hidden="true"
        >
          <div className="login-gradient-deep absolute inset-[-35%] bg-[radial-gradient(ellipse_at_15%_18%,#0B1739_0%,#102A43_30%,transparent_64%),radial-gradient(ellipse_at_82%_78%,#163B5C_0%,#1E4D6B_28%,transparent_67%)] blur-3xl" style={{ animation: 'loginGradientDeep 19s ease-in-out infinite alternate' }} />
          <div className="login-gradient-aurora absolute inset-[-38%] bg-[radial-gradient(ellipse_at_76%_22%,#A9C7DF_0%,#DCEAF5_24%,transparent_60%),radial-gradient(ellipse_at_30%_76%,#256B78_0%,#4F7CAC_28%,transparent_65%)] blur-3xl" style={{ animation: 'loginGradientAurora 14s ease-in-out infinite alternate' }} />
          <div className="login-gradient-pearl absolute inset-[-42%] bg-[radial-gradient(ellipse_at_70%_46%,#DCEAF5_0%,#A9C7DF_18%,transparent_52%),radial-gradient(ellipse_at_28%_12%,#F8FAFC_0%,transparent_42%)] blur-[70px]" style={{ animation: 'loginGradientPearl 22s ease-in-out infinite alternate' }} />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,18,45,0.24)_0%,transparent_42%,rgba(16,42,67,0.12)_100%)]" />
          <div className="relative z-10 flex h-full w-full max-w-3xl flex-col justify-center px-10 py-12 xl:px-16">
            <div className="mb-8 max-w-md text-white">
              <h1 className="text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">Todas tus conversaciones,<br />en un solo lugar.</h1>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70">Una vista clara para acompañar cada conversación y atender a tus clientes con contexto.</p>
            </div>

            <div
              ref={crmCardRef}
              onMouseMove={updateCrmCardTilt}
              onMouseLeave={resetCrmCardTilt}
              className="login-crm-card -mx-24 w-[calc(100%+12rem)] overflow-hidden rounded-2xl border border-white/40 bg-white/10 p-1 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.55)] hover:shadow-[0_34px_88px_-24px_rgba(15,23,42,0.65)]"
            >
              <img
                src="/assets/login-crm-cajero.png"
                alt="Vista del CRM de cajero"
                className="block h-auto max-h-[min(60vh,620px)] w-full rounded-xl object-contain"
              />
            </div>
          </div>
        </section>

        <section className="flex min-h-screen w-full items-center justify-center bg-slate-50 px-5 py-10 dark:bg-slate-950 sm:px-8 lg:w-[42%] lg:px-10">
          <div className="w-full max-w-[440px]">
            <div className="mb-8 flex flex-col items-center text-center">
              <img src="/logo.png" alt="Adphantom" className="h-12 w-12 rounded-xl object-contain" />
              <p className="mt-2 text-sm font-semibold tracking-wide text-slate-800 dark:text-slate-200">Adphantom</p>
            </div>
            <div className="mb-8 flex flex-col items-center text-center lg:items-start lg:text-left">
              <h2 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Bienvenido</h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Ingresá a tu espacio de trabajo.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">Correo electrónico</Label>
                <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:border-indigo-400 focus-visible:ring-2 focus-visible:ring-indigo-500/20 dark:bg-slate-800/70 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus-visible:border-indigo-400 dark:focus-visible:ring-indigo-500/20" placeholder="tu@mailasignado.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Contraseña</Label>
                <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:border-indigo-400 focus-visible:ring-2 focus-visible:ring-indigo-500/20 dark:bg-slate-800/70 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus-visible:border-indigo-400 dark:focus-visible:ring-indigo-500/20" placeholder="********" />
              </div>
              <Button type="submit" data-testid="login-submit-button" disabled={loading} className="h-11 w-full rounded-xl bg-indigo-600 font-medium text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:disabled:bg-slate-800 dark:disabled:text-slate-500">{loading ? 'Iniciando sesión...' : 'Iniciar sesión'}</Button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
